<script setup lang="ts">
	import { useQueryClient } from '@tanstack/vue-query'
	import { History } from 'lucide-vue-next'
	import { computed, provide, ref, watch } from 'vue'
	import type { Component } from 'vue'
	import { useRouter } from 'vue-router'

	import BreadCrumb, { type Page } from '@/components/BreadCrumb.vue'
	import EditTabLayout, { type TabDef } from '@/components/EditTabLayout.vue'
	import LoadingModal from '@/components/LoadingModal.vue'

	import useAuthUser from '@/composables/useAuthUser'
	import useHasPermissionUser from '@/composables/useHasPermissionUser'

	import useUser from '@/modules/users/composables/useUser'

	import AccountSettingsTab from './partials/edit/AccountSettingsTab.vue'
	import BillingInformationTab from './partials/edit/BillingInformationTab.vue'
	import CardsTab from './partials/edit/CardsTab.vue'
	import ContactMethodsTab from './partials/edit/ContactMethodsTab.vue'
	import DocumentsTab from './partials/edit/DocumentsTab.vue'
	import HotelLoyaltyTab from './partials/edit/HotelLoyaltyTab.vue'
	import LinkDepartmentAndCostCenterTab from './partials/edit/LinkDepartmentAndCostCenterTab.vue'
	import NotificationsTab from './partials/edit/NotificationsTab.vue'
	import PersonalInformationTab from './partials/edit/PersonalInformationTab.vue'
	import WorkInformationTab from './partials/edit/WorkInformationTab.vue'

	const router = useRouter()

	interface TabConfig {
		name: string
		/** Encabezado bajo el que se agrupa en la barra lateral (ver EditTabLayout). */
		group?: string
		value: string
		hasPermission: string[]
		entityType?: string
		component: Component
		alwaysCheckPermissions?: boolean
	}

	enum ViewPermission {
		UpdateUsers = 'update-users',
		ViewEmails = 'view-emails',
		ViewPhones = 'view-phones',
		Memberships = 'view-memberships',
		Documents = 'view-documents',
		BillingInformation = 'view-billingInformation',
		ViewDepartments = 'view-departments',
		ViewCostCenters = 'view-costCenters',
		CreateCards = 'create-cards',
	}

	enum EntityType {
		Company = 'App\\Models\\Company',
		User = 'App\\Models\\User',
	}

	const TAB_CONFIGS: TabConfig[] = [
		{
			name: 'Información personal',
			group: 'Perfil',
			value: 'personal-information',
			hasPermission: [ViewPermission.UpdateUsers],
			entityType: EntityType.Company,
			component: PersonalInformationTab,
		},
		{
			name: 'Información Laboral',
			group: 'Perfil',
			value: 'work-information',
			hasPermission: [ViewPermission.UpdateUsers],
			entityType: EntityType.Company,
			component: WorkInformationTab,
			// Un Traveler editando su propio perfil no gestiona supervisor/roles:
			// sin esto, el bypass de "isSelf" dejaría ver un tab vacío.
			alwaysCheckPermissions: true,
		},
		{
			name: 'Medios de contacto',
			group: 'Perfil',
			value: 'contact-methods',
			hasPermission: [ViewPermission.ViewEmails, ViewPermission.ViewPhones],
			entityType: EntityType.User,
			component: ContactMethodsTab,
		},
		{
			name: 'Documentos',
			group: 'Perfil',
			value: 'documents',
			hasPermission: [ViewPermission.Documents],
			entityType: EntityType.User,
			component: DocumentsTab,
		},
		{
			name: 'Programas de lealtad',
			group: 'Perfil',
			value: 'hotel-loyalty',
			hasPermission: [ViewPermission.Memberships],
			entityType: EntityType.User,
			component: HotelLoyaltyTab,
		},
		// "Mis datos fiscales", no "Información fiscal": el cliente reportó que los
		// usuarios lo confundían con los datos fiscales de la empresa y creían que
		// tenían que volver a capturarlos. Es la facturación personal del viajero.
		{
			name: 'Mis datos fiscales',
			group: 'Perfil',
			value: 'billing-information',
			hasPermission: [ViewPermission.BillingInformation],
			entityType: EntityType.User,
			component: BillingInformationTab,
		},
		{
			name: 'Asignación Organizacional',
			group: 'Empresa',
			value: 'org-assignment',
			hasPermission: [
				ViewPermission.ViewDepartments,
				ViewPermission.ViewCostCenters,
			],
			entityType: EntityType.Company,
			component: LinkDepartmentAndCostCenterTab,
			alwaysCheckPermissions: true,
		},
		{
			name: 'Tarjetas bancarias',
			group: 'Empresa',
			value: 'cards',
			hasPermission: [ViewPermission.CreateCards],
			entityType: EntityType.User,
			component: CardsTab,
		},
		{
			name: 'Ajustes de la cuenta',
			group: 'Cuenta y seguridad',
			value: 'account-settings',
			hasPermission: [ViewPermission.UpdateUsers],
			entityType: EntityType.Company,
			component: AccountSettingsTab,
		},
		{
			name: 'Notificaciones',
			group: 'Cuenta y seguridad',
			value: 'notifications',
			hasPermission: [ViewPermission.ViewEmails, ViewPermission.ViewPhones],
			entityType: EntityType.User,
			component: NotificationsTab,
		},
	]

	const { user, isLoading, isFetching, refetch } = useUser({
		immediate: true,
		context: 'profile',
	})

	const pages = ref<Page[]>([])
	const { hasPermissionUser, abilities, redirectTo404 } = useHasPermissionUser()
	const { user: userLogged } = useAuthUser()

	const visibleTabs = computed<TabConfig[]>(() =>
		TAB_CONFIGS.filter((tab) => {
			const isSelf = user.value?.id === userLogged.value?.id
			if (isSelf && tab.hasPermission.length === 0) return true
			return tab.hasPermission.some((permission) => {
				if (isSelf && !tab.alwaysCheckPermissions) return true
				return (
					tab.entityType != null &&
					hasPermissionUser(permission, tab.entityType)
				)
			})
		})
	)

	const tabDefs = computed<TabDef[]>(() =>
		visibleTabs.value.map((tab) => ({
			value: tab.value,
			label: tab.name,
			group: tab.group,
		}))
	)

	const queryClient = useQueryClient()

	const invalidateQueryData = async () => {
		queryClient.invalidateQueries(['user', Number(user.value?.id)])
		queryClient.invalidateQueries(['user'])
		await refetch()
	}

	provide('editUserProvides', { invalidateQueryData, isFetching })

	watch(
		user,
		(value) => {
			if (!value) return
			pages.value = [
				{ name: 'Usuarios', to: { name: 'users.index' } },
				{
					name: value.fullName,
					to: { name: 'users.show', params: { id: value.id } },
				},
				{
					name: 'Editar',
					to: { name: 'users.edit', params: { id: value.id } },
					current: true,
				},
			]
		},
		{ immediate: true }
	)

	watch(
		abilities,
		() => {
			if (userLogged.value?.id === Number(router.currentRoute.value.params.id))
				return
			redirectTo404(ViewPermission.UpdateUsers, EntityType.Company)
		},
		{ immediate: true }
	)
</script>

<template>
	<BreadCrumb :pages="pages" />

	<div
		v-if="user"
		class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6"
	>
		<EditTabLayout :tabs="tabDefs">
			<!-- Identity card -->
			<template #identity>
				<RouterLink
					:to="{ name: 'users.show', params: { id: user.id } }"
					class="flex items-center gap-3 group"
				>
					<img
						:src="
							user.avatar ||
							`https://ui-avatars.com/api/?name=${user.name}&background=021F58&color=fff`
						"
						:alt="user.fullName"
						class="w-11 h-11 rounded-full object-cover ring-2 ring-gray-100 flex-shrink-0"
					/>
					<div class="min-w-0">
						<p
							class="text-sm font-semibold text-gray-900 leading-tight truncate"
						>
							{{ user.fullName }}
						</p>
						<span
							class="text-[11px] text-[#253C70] hover:underline flex items-center gap-0.5 mt-0.5"
						>
							<span class="material-symbols-outlined text-[11px]"
								>open_in_new</span
							>
							Ver perfil
						</span>
					</div>
				</RouterLink>
			</template>

			<!-- Tab content panels -->
			<template
				v-for="tab in visibleTabs"
				:key="tab.value"
				#[`tab-${tab.value}`]
			>
				<component
					:is="tab.component"
					:user="user"
				/>
			</template>
		</EditTabLayout>
	</div>

	<LoadingModal :when="isLoading" />
</template>
